import type { Document, DocumentInfo } from './types'

interface CacheEntry<T> {
  data: T
  timestamp: number
  size: number
}

/**
 * LRU Cache for document storage
 * Single Responsibility: Manage in-memory caching with size limits
 */
export class CacheManager {
  private cache: Map<string, CacheEntry<Document>> = new Map()
  private metaCache: Map<string, CacheEntry<DocumentInfo>> = new Map()
  private accessOrder: string[] = []
  private currentSize = 0
  private readonly maxSize: number
  private readonly maxItems: number
  private readonly ttl: number // Time to live in milliseconds
  
  constructor(
    maxSizeMB: number = 50, // 50MB default
    maxItems: number = 100,
    ttlMinutes: number = 30
  ) {
    this.maxSize = maxSizeMB * 1024 * 1024 // Convert to bytes
    this.maxItems = maxItems
    this.ttl = ttlMinutes * 60 * 1000 // Convert to milliseconds
  }
  
  /**
   * Get document from cache
   */
  get(id: string): Document | null {
    const entry = this.cache.get(id)
    
    if (!entry) return null
    
    // Check if expired
    if (this.isExpired(entry)) {
      this.remove(id)
      return null
    }
    
    // Update access order (LRU)
    this.updateAccessOrder(id)
    
    return entry.data
  }
  
  /**
   * Get document info from cache
   */
  getInfo(id: string): DocumentInfo | null {
    const entry = this.metaCache.get(id)
    
    if (!entry) return null
    
    // Check if expired
    if (this.isExpired(entry)) {
      this.metaCache.delete(id)
      return null
    }
    
    return entry.data
  }
  
  /**
   * Set document in cache
   */
  set(id: string, document: Document): void {
    const size = this.calculateSize(document)
    
    // Check if single item exceeds max size
    if (size > this.maxSize) {
      console.warn(`Document ${id} exceeds cache size limit`)
      return
    }
    
    // Remove old entry if exists
    if (this.cache.has(id)) {
      this.remove(id)
    }
    
    // Evict items if necessary
    while (this.currentSize + size > this.maxSize || this.cache.size >= this.maxItems) {
      this.evictLRU()
    }
    
    // Add to cache
    const entry: CacheEntry<Document> = {
      data: document,
      timestamp: Date.now(),
      size
    }
    
    this.cache.set(id, entry)
    this.currentSize += size
    this.updateAccessOrder(id)
    
    // Also cache metadata
    this.setInfo(id, this.extractInfo(document))
  }
  
  /**
   * Set document info in cache
   */
  setInfo(id: string, info: DocumentInfo): void {
    const size = this.calculateSize(info)
    
    const entry: CacheEntry<DocumentInfo> = {
      data: info,
      timestamp: Date.now(),
      size
    }
    
    this.metaCache.set(id, entry)
  }
  
  /**
   * Remove document from cache
   */
  remove(id: string): void {
    const entry = this.cache.get(id)
    if (entry) {
      this.currentSize -= entry.size
      this.cache.delete(id)
      this.metaCache.delete(id)
      
      // Remove from access order
      const index = this.accessOrder.indexOf(id)
      if (index > -1) {
        this.accessOrder.splice(index, 1)
      }
    }
  }
  
  /**
   * Clear entire cache
   */
  clear(): void {
    this.cache.clear()
    this.metaCache.clear()
    this.accessOrder = []
    this.currentSize = 0
  }
  
  /**
   * Get all cached document IDs
   */
  getKeys(): string[] {
    return Array.from(this.cache.keys())
  }
  
  /**
   * Get all cached document infos
   */
  getAllInfos(): DocumentInfo[] {
    const infos: DocumentInfo[] = []
    
    for (const [id, entry] of this.metaCache) {
      if (!this.isExpired(entry)) {
        infos.push(entry.data)
      } else {
        this.metaCache.delete(id)
      }
    }
    
    return infos
  }
  
  /**
   * Check if document is cached
   */
  has(id: string): boolean {
    const entry = this.cache.get(id)
    if (!entry) return false
    
    if (this.isExpired(entry)) {
      this.remove(id)
      return false
    }
    
    return true
  }
  
  /**
   * Get cache statistics
   */
  getStats(): {
    itemCount: number
    totalSize: number
    maxSize: number
    hitRate: number
  } {
    return {
      itemCount: this.cache.size,
      totalSize: this.currentSize,
      maxSize: this.maxSize,
      hitRate: this.calculateHitRate()
    }
  }
  
  // Private methods
  
  private isExpired(entry: CacheEntry<any>): boolean {
    return Date.now() - entry.timestamp > this.ttl
  }
  
  private calculateSize(obj: any): number {
    // Rough estimation of object size in bytes
    const str = JSON.stringify(obj)
    return new Blob([str]).size
  }
  
  private updateAccessOrder(id: string): void {
    // Remove from current position
    const index = this.accessOrder.indexOf(id)
    if (index > -1) {
      this.accessOrder.splice(index, 1)
    }
    
    // Add to end (most recently used)
    this.accessOrder.push(id)
  }
  
  private evictLRU(): void {
    if (this.accessOrder.length === 0) return
    
    // Remove least recently used (first item)
    const lruId = this.accessOrder[0]
    this.remove(lruId)
  }
  
  private extractInfo(document: Document): DocumentInfo {
    return {
      id: document.id,
      title: document.title,
      preview: document.content.substring(0, 200),
      createdAt: document.createdAt,
      updatedAt: document.updatedAt,
      wordCount: document.metadata.wordCount,
      tags: document.metadata.tags
    }
  }
  
  private hitCount = 0
  private missCount = 0
  
  private calculateHitRate(): number {
    const total = this.hitCount + this.missCount
    if (total === 0) return 0
    return (this.hitCount / total) * 100
  }
  
  // Public methods for hit rate tracking
  recordHit(): void {
    this.hitCount++
  }
  
  recordMiss(): void {
    this.missCount++
  }
}

// Singleton instance
export const cacheManager = new CacheManager()